/* eslint-disable no-console */
import React, { useState, useRef, useEffect } from 'react';
import { useGlobal, TextField } from '@rcg/ui-react';
import clsx from 'clsx';
import { Formik, Form } from 'formik';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import InputAdornment from '@material-ui/core/InputAdornment';
import { usePaymentInputs } from 'react-payment-inputs';
import images from 'react-payment-inputs/images';
import Modal from '@material-ui/core/Modal';
import { navigate } from '@gatsbyjs/reach-router';
import { useQuery } from '@apollo/client';
import { Base64 } from 'js-base64';
import { formatPrice } from '@rcg/pricing-format';
import { cookieCountry } from '../../utils/globalization/marketCookies';
import { globalizationPath } from '../../utils/globalization/globalizationPath';
import AirLoader from '../AirLoader';
import useDictionary from '../../hooks/useDictionary';
import Button from '../UiComponents/Button';
import useStyles from './useStyles';
import Dictionary from '../../utils/dictionary';
import {
  heldConfirmInputMap,
  paymentValidationSchema,
  supportedCreditCardMap,
  tokenizeCardAndBookFlight,
} from '../../utils/payment';
import { cacheClean } from '../../utils/common';
import setPayment from '../../operations/localState/setPayment';
import {
  NAVIGATION_PATHS,
  APPLICATION_NAME,
  BOOKING_COUNTRY,
  ENABLE_CVV_COLLECTION,
} from '../../utils/constants';
import useBookFlight from '../../operations/dataAccess/useBookFlight';
import { ENABLE_GIFT_CARD_TOKENIZATION, LDUser } from '../../utils/ldUser';
import {
  bookingProfileVar,
  heldReservationVar,
  searchParamsVar,
  globalDataVar,
} from '../../operations/localState/reactiveVariables';
import setBookedReservation from '../../operations/localState/setBookedReservation';
import { trackPaymentView } from '../../utils/analytics/searchTracking';
import { LAUNCH_DARKLY_FLAGS_AIR } from '../../operations/queries';
import getBrand from '../../utils/getBrand';
import InfoTooltip from '../InfoTooltip/index';
import { ReactComponent as CardIconFront } from '../../assets/svg/cc-illustration-fourCVV.svg';
import { ReactComponent as CardIconBack } from '../../assets/svg/cc-illustration-threeCVV.svg';

const currentBrand = getBrand();
const CustomErrorMessage = ({ message, className }) => {
  const classes = useStyles();
  return <div className={clsx(classes.errorMessage, className)}>{message}</div>;
};
const ZeroValue = 0;

const CustomInput = ({
  name,
  label,
  placeholder,
  value,
  onChange,
  icon = null,
  className,
  error,
  errorMessage,
  inputProps = {},
  onBlur,
  disabled,
}) => {
  const classes = useStyles();
  const [inputValue, setInputValue] = useState(value);
  const iconAdornment = icon ? <InputAdornment position="end">{icon}</InputAdornment> : null;
  const handleChange = (event) => {
    setInputValue(event.target.value);
    onChange(event);
  };
  return (
    <div
      className={clsx(classes.inputWrapper, 'inputWrapper', className, error ? 'input-error' : '')}>
      <div className={classes.inputLabel}>{label}</div>
      <div className={clsx(classes.inputBox, 'inputBox')}>
        <OutlinedInput
          id={name}
          name={name}
          variant="outlined"
          fullWidth
          placeholder={placeholder}
          endAdornment={iconAdornment}
          value={inputValue}
          onChange={handleChange}
          inputProps={inputProps}
          onBlur={onBlur}
          disabled={disabled}
        />
      </div>
      {error ? <CustomErrorMessage message={errorMessage} /> : ''}
    </div>
  );
};

const CustomSelect = ({
  name,
  handleChange,
  selectedValue,
  defaultValue,
  options,
  className,
  onBlur,
  disabled,
}) => {
  const classes = useStyles();
  return (
    <FormControl
      variant="outlined"
      className={clsx(
        classes.dateSelectInput,
        selectedValue === '' ? classes.selectDefaultValue : '',
        'dateSelectInput',
        className,
      )}
      onBlur={onBlur}>
      <Select
        data-testid={`select-${defaultValue}`}
        value={selectedValue}
        name={name}
        onChange={handleChange}
        displayEmpty
        IconComponent={KeyboardArrowDownIcon}
        native
        disabled={disabled}>
        <option value="">{defaultValue}</option>
        {options.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </Select>
    </FormControl>
  );
};

const CustomDatesInput = ({
  label,
  monthValue,
  handleMonthChange,
  yearValue,
  handleYearChange,
  nameMonth,
  nameYear,
  errorMonth,
  errorYear,
  onBlur,
  errorMessageDate,
  disabled,
}) => {
  const classes = useStyles();
  const {
    data: { localizedDayjs },
  } = useGlobal();
  const getDictionary = useDictionary(Dictionary);
  const [month, setMonth] = React.useState(monthValue);
  const [year, setYear] = React.useState(yearValue);
  const months = localizedDayjs.localeData().monthsShort();
  const now = new Date().getUTCFullYear();
  const years = Array(now - (now - 21))
    .fill('')
    .map((_v, idx) => now + idx);
  const handleMonthChangeInternally = (event) => {
    setMonth(event.target.value);
    handleMonthChange(months.findIndex((monthLabel) => monthLabel === event.target.value) + 1);
  };
  const handleYearChangeInternally = (event) => {
    setYear(event.target.value);
    handleYearChange(event);
  };
  return (
    <div className={clsx(classes.inputWrapper, 'inputWrapper')}>
      <div className={classes.inputLabel}>{label}</div>
      <div className={clsx(classes.inputBox, 'inputSelectBox')}>
        <CustomSelect
          name={nameMonth}
          handleChange={handleMonthChangeInternally}
          selectedValue={month}
          defaultValue={getDictionary(
            Dictionary.airPaymentCheckoutExpirationDateMonthPlaceholder.key,
          )}
          options={months}
          className={errorMonth ? 'input-error' : ''}
          onBlur={onBlur}
          disabled={disabled}
        />
        <CustomSelect
          name={nameYear}
          handleChange={handleYearChangeInternally}
          selectedValue={year}
          defaultValue={getDictionary(
            Dictionary.airPaymentCheckoutExpirationDateYearPlaceholder.key,
          )}
          options={years}
          className={errorYear ? 'input-error' : ''}
          onBlur={onBlur}
          disabled={disabled}
        />
      </div>
      {errorMonth || errorYear ? <CustomErrorMessage message={errorMessageDate} /> : ''}
    </div>
  );
};

export const CreditCardInformationForm = ({
  onFormChange,
  paymentSelection,
  handleSubmitForm,
  amountToPay,
  heldConfirmInput,
  bookFlight,
  bookingProfile,
  setIsLoading,
  showCancelPaywayBanner,
  showErrorvalidationBanner,
  payment3dsToggle,
  disableForm,
  giftCards,
  royalPayway,
  paymentMethodFormRef = null,
  showLineError = false,
  onFinishCreditCardFormSubmit,
}) => {
  const classes = useStyles();
  const formRef = useRef();
  const { data } = useGlobal();
  const getDictionary = useDictionary(Dictionary);
  const launchDarklyParameters = JSON.parse(sessionStorage.getItem(BOOKING_COUNTRY)) || {
    country: 'USA',
    officeID: 'mia',
  };
  const { data: launchDarklyFlags } = useQuery(LAUNCH_DARKLY_FLAGS_AIR, {
    fetchPolicy: 'no-cache',
    variables: {
      ldUser: LDUser(),
      language: data.lang,
      bookingCountry: launchDarklyParameters?.country,
      clientApp: APPLICATION_NAME,
      application: APPLICATION_NAME,
      officeID: launchDarklyParameters?.officeID,
      brand: currentBrand.ldBrand,
      country: cookieCountry().toUpperCase(),
    },
  });

  const ERROR_MESSAGES = {
    emptyCardNumber: getDictionary(Dictionary.airPaymentCheckoutCardNumberErrorRequired.key),
    invalidCardNumber: getDictionary(Dictionary.airPaymentCheckoutCardNumberErrorInvalid.key),
  };
  const { meta, getCardNumberProps, getCardImageProps } = usePaymentInputs({
    errorMessages: ERROR_MESSAGES,
  });

  useEffect(() => {
    handleSubmitForm(formRef);
    // eslint-disable-next-line
  }, [paymentSelection]);

  useEffect(() => {
    if (disableForm) {
      formRef.current.resetForm();
    }
  }, [disableForm, formRef]);

  const getCreditCardAmount = () => {
    return Array.isArray(giftCards) && giftCards.length > 0
      ? giftCards.reduce((total, giftCard) => {
          const totalToPay = total - giftCard.chargedNotFormatted;
          return totalToPay <= ZeroValue ? ZeroValue : totalToPay;
        }, amountToPay)
      : amountToPay;
  };

  const cvvTooltipContent = (
    <div className={classes.tooltipContent}>
      <div className="icons-container">
        <div className="icon">
          <span>{getDictionary(Dictionary.airPaymentCvvHelperBack.key)}</span>
          <CardIconBack />
        </div>
        <div className="icon">
          <span>{getDictionary(Dictionary.airPaymentCvvHelperFront.key)}</span>
          <CardIconFront />
        </div>
      </div>
      <span>{getDictionary(Dictionary.airPaymentCvvHelper.key)}</span>
    </div>
  );

  const cvvLabel = (
    <div className={classes.cvvLabel}>
      {getDictionary(Dictionary.airPaymentCheckoutCVVLabel.key)}
      <InfoTooltip description={cvvTooltipContent} />
    </div>
  );

  const asingCreditCard = (
    cardNumber,
    firstNameOnCard,
    lastNameOnCard,
    expiryMonth,
    expiryYear,
    authenticationVar,
    isThreeDsEligible,
    creditCardAmount,
    cvv,
  ) => {
    return {
      cardType:
        (meta.cardType && meta.cardType.type && supportedCreditCardMap[meta.cardType.type]) || '',
      cardNumber: cardNumber.replace(/\s/g, ''),
      expiration: `${expiryMonth < 10 ? '0' : ''}${expiryMonth}${expiryYear.slice(-2)}`,
      amount: creditCardAmount,
      firstNameOnCard,
      lastNameOnCard,
      authentication: Base64.encode(authenticationVar),
      isThreeDsEligible,
      paymentMethod: 'CC',
      cvv,
    };
  };

  const onFinishTokenizeAndBook = () => {
    onFinishCreditCardFormSubmit();
  };

  const handleBookFlightApiCall = async ({
    cardNumber,
    firstNameOnCard,
    lastNameOnCard,
    expiryMonth,
    expiryYear,
    authentication,
    isThreeDsEligible,
    cvv,
  }) => {
    setIsLoading(true);
    const ldAirFlagsThreeDSGiftCard = launchDarklyFlags?.ldFlags[ENABLE_GIFT_CARD_TOKENIZATION];
    const authenticationVar = authentication ? JSON.stringify(authentication) : '';
    const creditCardAmount = getCreditCardAmount();
    const creditCard = asingCreditCard(
      cardNumber,
      firstNameOnCard,
      lastNameOnCard,
      expiryMonth,
      expiryYear,
      authenticationVar,
      isThreeDsEligible,
      creditCardAmount,
      cvv,
    );
    const paymentInputArray = [creditCard];
    const giftCardsFor3ds = [];
    if (giftCards && giftCards.length) {
      giftCards.forEach((giftcard) => {
        const giftCardNumber = giftcard.info.cardNumber.replace(/\s/g, '');
        const globalData = globalDataVar();
        paymentInputArray.push({
          cardType: 'VA',
          cardNumber: giftCardNumber,
          expiration: `${giftcard.info.expiryMonth < 10 ? '0' : ''}${
            giftcard.info.expiryMonth
          }${giftcard.info.expiryYear.slice(-2)}`,
          amount: giftcard.chargedNotFormatted,
          cvv: giftcard.info.securityCode,
          paymentMethod: 'GC',
        });
        giftCardsFor3ds.push({
          correlationId: giftCardNumber.substr(-4),
          order: {
            bookingId: String(+bookingProfileVar().flightProfile.id),
            currency: String(bookingProfile.flightProfile.currency),
            country: globalData.country,
          },
          paymentMethod: {
            cardNumber: giftCardNumber,
            expirationYear: giftcard.info.expiryYear,
            expirationMonth: `${giftcard.info.expiryMonth < 10 ? '0' : ''}${
              giftcard.info.expiryMonth
            }`,
          },
        });
      });
      if (ldAirFlagsThreeDSGiftCard) {
        const threeDsComponentElement = royalPayway && royalPayway.current && royalPayway.current;
        tokenizeCardAndBookFlight(
          threeDsComponentElement,
          giftCardsFor3ds,
          paymentInputArray,
          bookFlight,
          heldConfirmInput,
          showErrorvalidationBanner,
          setIsLoading,
          creditCard,
          onFinishTokenizeAndBook,
        );
      } else {
        await bookFlight({
          variables: {
            heldConfirmInput,
            paymentInput: paymentInputArray,
          },
        });
        onFinishCreditCardFormSubmit();
        setIsLoading(false);
      }
    } else {
      await bookFlight({
        variables: {
          heldConfirmInput,
          paymentInput: paymentInputArray,
        },
      });
      onFinishCreditCardFormSubmit();
      setIsLoading(false);
    }
  };

  const runGatewayPaywayValidation = ({
    cardNumber,
    firstNameOnCard,
    lastNameOnCard,
    expiryMonth,
    expiryYear,
    cvv,
  }) => {
    const threeDsComponentElement = royalPayway && royalPayway.current && royalPayway.current;
    // trigger initiateAndAuthenticate on form submit.
    setIsLoading(true);
    const globalData = globalDataVar();

    threeDsComponentElement
      .initiateAndAuthenticate({
        // correlationId: heldConfirmInput.correlationID,
        order: {
          amount: amountToPay,
          country: globalData.country,
          currency: String(bookingProfile.flightProfile.currency),
          bookingId: String(+bookingProfileVar().flightProfile.id),
          // Modal validation works whit this currencies: USD, NDK, DKK, GBP, EUR
          // referenceId: 'SHOREX-12345', // optional
        },
        paymentMethod: {
          cardNumber: String(cardNumber),
          cardholder: `${firstNameOnCard} ${lastNameOnCard}`,
          // or cardToken: '2223002562670007',
          expirationMonth: `${expiryMonth < 10 ? '0' : ''}${expiryMonth}`,
          expirationYear: String(expiryYear),
        },
      })
      .then((threeDsSession) => {
        const values = {
          cardNumber,
          firstNameOnCard,
          lastNameOnCard,
          expiryMonth,
          expiryYear,
          authentication: threeDsSession.authentication,
          isThreeDsEligible: threeDsSession.isThreeDsEligible,
          cvv,
        };
        switch (threeDsSession.status) {
          case 'SKIPPED':
          case 'AUTH_COMPLETED':
            handleBookFlightApiCall(values);
            break;
          case 'CANCELLED':
            showCancelPaywayBanner();
            break;
          case 'DECLINED':
            showErrorvalidationBanner();
            break;
          case 'ERROR':
            showErrorvalidationBanner();
            break;
          default:
            break;
        }
      })
      .catch(() => {
        showErrorvalidationBanner();
      })
      .finally(() => {
        setIsLoading(false);
        onFinishCreditCardFormSubmit();
      });
  };
  const isCvvLDFlagsEnabled = launchDarklyFlags?.ldFlags[ENABLE_CVV_COLLECTION];

  let confitionalvalues = {
    firstNameOnCard: '',
    lastNameOnCard: '',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
  };

  let conditionalSchema = {
    airPaymentCheckoutFirstNameErrorRequired: getDictionary(
      Dictionary.airPaymentCheckoutFirstNameErrorRequired.key,
    ),
    airPaymentCheckoutLastNameErrorRequired: getDictionary(
      Dictionary.airPaymentCheckoutLastNameErrorRequired.key,
    ),
    airPaymentCheckoutCardNumberErrorRequired: getDictionary(
      Dictionary.airPaymentCheckoutCardNumberErrorRequired.key,
    ),
    airPaymentCheckoutExpireMonthErrorRequired: getDictionary(
      Dictionary.airPaymentCheckoutExpirationDateError.key,
    ),
    airPaymentCheckoutExpireYearErrorRequired: getDictionary(
      Dictionary.airPaymentCheckoutExpirationDateError.key,
    ),
  };

  if (isCvvLDFlagsEnabled) {
    confitionalvalues = { ...confitionalvalues, cvv: '' };
    conditionalSchema = {
      ...conditionalSchema,
      airPaymentCvvErrorRequired: getDictionary(Dictionary.airPaymentCheckoutCVVError.key),
    };
  }

  return (
    <Formik
      enableReinitialize
      innerRef={(fmRef) => {
        formRef.current = fmRef;
        if (paymentMethodFormRef) {
          // eslint-disable-next-line no-param-reassign
          paymentMethodFormRef.current = fmRef;
        }
      }}
      initialValues={confitionalvalues}
      validationSchema={paymentValidationSchema(conditionalSchema)}
      validate={(values) => {
        const errors = {};

        const onlyLettersRgx = /^[A-Za-z]+$/;
        const onlyNumberxRgx = /^\d{1,4}$/;

        const isFirstNameonCardError =
          values.firstNameOnCard &&
          (!onlyLettersRgx.test(values.firstNameOnCard) || values.firstNameOnCard.length > 14);

        const isLastNameOnCardError =
          values.lastNameOnCard &&
          (!onlyLettersRgx.test(values.lastNameOnCard) || values.lastNameOnCard.length > 19);

        const isCvvError = !onlyNumberxRgx.test(values.cvv) || values.cvv.length < 3 || !values.cvv;

        if (isFirstNameonCardError) {
          errors.firstNameOnCard = getDictionary(
            Dictionary.airPaymentCheckoutFirstNameErrorInvalid.key,
          );
        }
        if (isLastNameOnCardError) {
          errors.lastNameOnCard = getDictionary(
            Dictionary.airPaymentCheckoutLastNameErrorInvalid.key,
          );
        }
        if (values.expiryMonth && values.expiryYear) {
          const position = values.expiryMonth - 1;
          const currentDate = new Date();
          const selectedDate = new Date();
          selectedDate.setMonth(position);
          selectedDate.setYear(values.expiryYear);
          if (selectedDate < currentDate) {
            errors.expiryMonth = getDictionary(
              Dictionary.airPaymentCheckoutExpirationDateErrorInvalid.key,
            );
          }
        }
        if (isCvvError && isCvvLDFlagsEnabled) {
          errors.cvv = getDictionary(Dictionary.airPaymentCheckoutCVVError.key);
        }
        return errors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        const { errors } = formRef.current;

        if (showLineError || Object.keys(errors).length) return;

        setSubmitting(false);
        if (!meta.error) {
          setPayment({
            method: paymentSelection,
          });
          // If it is true, the 3ds component should validate the credit card information.
          if (payment3dsToggle) {
            runGatewayPaywayValidation(values);
          } else {
            setIsLoading(true);
            handleBookFlightApiCall(values);
          }
        }
      }}>
      {({ errors, touched, values, handleSubmit, handleChange, handleBlur, setFieldValue }) => (
        <Form
          id="submit-checkout-form"
          data-testid="creditcardForm"
          ref={formRef}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}>
          {onFormChange({ card: meta, errors, values })}
          <div className={classes.inputBoxWrapper}>
            <CustomInput
              id="first-name"
              name="firstNameOnCard"
              label={getDictionary(Dictionary.airPaymentCheckoutFirstNameLabel.key)}
              placeholder={getDictionary(
                Dictionary.airPaymentCheckoutEnterFirstNamePlaceholder.key,
              )}
              value={values.firstNameOnCard}
              onChange={(event) => setFieldValue('firstNameOnCard', event.target.value)}
              error={errors.firstNameOnCard && touched.firstNameOnCard}
              errorMessage={errors.firstNameOnCard}
              inputProps={{
                'data-testid': 'first-name',
              }}
              onBlur={handleBlur}
              disabled={disableForm}
            />
            <CustomInput
              id="last-name"
              name="lastNameOnCard"
              label={getDictionary(Dictionary.airPaymentCheckoutLastNameLabel.key)}
              placeholder={getDictionary(Dictionary.airPaymentCheckoutEnterLastNamePlaceholder.key)}
              value={values.lastNameOnCard}
              onChange={(event) => setFieldValue('lastNameOnCard', event.target.value)}
              error={errors.lastNameOnCard && touched.lastNameOnCard}
              errorMessage={errors.lastNameOnCard}
              inputProps={{
                'data-testid': 'last-name',
              }}
              onBlur={handleBlur}
              disabled={disableForm}
            />
          </div>

          <div className={classes.inputBoxWrapper}>
            <CustomInput
              id="card-number-id"
              name="cardNumber"
              label={getDictionary(Dictionary.airPaymentCheckoutCardNumberLabel.key)}
              placeholder={getDictionary(Dictionary.airPaymentCheckoutEnterCardPlaceholder.key)}
              value={values.cardNumber}
              onChange={(event) => setFieldValue('cardNumber', event.target.value)}
              icon={<svg {...getCardImageProps({ images })} />}
              className="no-right-padding"
              error={
                (meta.touchedInputs.cardNumber &&
                  typeof meta.erroredInputs.cardNumber !== 'undefined' &&
                  !disableForm) ||
                (errors.cardNumber && touched.cardNumber && !disableForm)
              }
              errorMessage={
                (meta.touchedInputs.cardNumber && meta.erroredInputs.cardNumber) ||
                errors.cardNumber
              }
              inputProps={{
                ...getCardNumberProps({
                  onChange: handleChange,
                  onBlur: handleBlur,
                }),
                'data-testid': 'card-number',
                placeholder: getDictionary(Dictionary.airPaymentCheckoutEnterCardPlaceholder.key),
              }}
              disabled={disableForm}
            />
            <div className={classes.bottomWrapper}>
              <CustomDatesInput
                label={getDictionary(Dictionary.airPaymentCheckoutExpirationDatePlaceholder.key)}
                nameMonth="expiryMonth"
                nameYear="expiryYear"
                monthValue={values.expiryMonth}
                handleMonthChange={(monthNumber) => setFieldValue('expiryMonth', monthNumber)}
                yearValue={values.expiryYear}
                handleYearChange={(event) => setFieldValue('expiryYear', event.target.value)}
                errorMonth={errors.expiryMonth && touched.expiryMonth}
                errorYear={errors.expiryYear && touched.expiryYear}
                errorMessageDate={errors.expiryMonth}
                onBlur={handleBlur}
                disabled={disableForm}
              />
              {isCvvLDFlagsEnabled && (
                <div className={classes.cvvWrapper}>
                  <TextField
                    name="cvv"
                    label={cvvLabel}
                    placeholder={getDictionary(Dictionary.airPaymentCheckoutCVVPlaceholder.key)}
                    value={values.cvv}
                    error={errors.cvv && touched.cvv}
                    helperText={touched.cvv && errors.cvv}
                    onChange={({ target: { value } }) =>
                      setFieldValue('cvv', /^\d{1,4}$/.test(value) ? value : '')
                    }
                    inputProps={{
                      maxLength: 4,
                      pattern: '[0-9]*',
                      'data-testid': 'cvv',
                      className: `${classes.textFieldField}`,
                    }}
                    InputLabelProps={{ shrink: true }}
                    onBlur={handleBlur}
                    disabled={disableForm}
                  />
                </div>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const enableCTA = (termsAndConditionsChecked, paymentSelection, noErrors) => {
  if (!termsAndConditionsChecked) {
    return false;
  }
  if (paymentSelection === 'reserve') {
    return true;
  }
  if (paymentSelection === 'payNow') {
    return noErrors;
  }
  return false;
};

export const TermsAndConditionsModal = ({ openModal, handleClose }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const getDictionary = useDictionary(Dictionary);

  useEffect(() => {
    setOpen(openModal);
  }, [openModal]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="Terms and Coditions Modal"
      aria-describedby="Terms and Coditions Modal"
      className={classes.termsAndConditionsModal}>
      <div className={classes.termsAndConditionsBody}>
        <div className={classes.termsAndConditionsTitle}>
          {getDictionary(Dictionary.airPaymentCheckoutTermsAndConditionsLabel.key)}
        </div>
        <div
          className={classes.termsAndConditionsContent}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: getDictionary(Dictionary.airTermsAndConditions.key),
          }}
        />
        <div className={classes.termsAndConditionsFooter}>
          <Button
            data-testid="ok-terms-and-conditions"
            onClick={handleClose}
            variant="contained"
            color="primary">
            {getDictionary(Dictionary.airPaymentCheckoutOkLabel.key)}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const CheckoutCard = ({
  holdFlightData,
  extraData,
  bookingProfile,
  showPaymentErrorBanner,
  showCancelPaywayBanner,
  showErrorvalidationBanner,
  payment3dsToggle,
  royalPayway,
  onFinishPayment,
}) => {
  cacheClean();
  const classes = useStyles();
  const getDictionary = useDictionary(Dictionary);
  const {
    data: { lang, localizedDayjs, market },
  } = useGlobal();
  const [isLoading, setIsLoading] = useState(false);
  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  const [paymentSelection, setPaymentSelection] = useState('reserve');
  const [termsAndConditionsChecked, setTermsAndConditionsChecked] = useState(false);
  const [noErrors, setNoErrors] = useState(false);
  const [openTermsAndConditionsModal, setOpenTermsAndConditionsModal] = useState(false);
  const { returnFlight } = extraData;
  const { priceChange } = holdFlightData;
  const checkoutRef = useRef(null);
  const heldConfirmInput = heldConfirmInputMap(
    bookingProfileVar,
    heldReservationVar,
    searchParamsVar,
  );
  const [bookFlight, { loading: bookLoading }] = useBookFlight({
    onCompleted: (data) => {
      setBookedReservation(data);
      onFinishPayment();
      navigate(`${urlPath}${NAVIGATION_PATHS.confirmation}`);
    },
    onError: (error) => {
      onFinishPayment();
      if (
        error &&
        Array.isArray(error.graphQLErrors) &&
        error.graphQLErrors.find(({ extensions }) => extensions.code === 'PAYMENT_ERROR')
      ) {
        showPaymentErrorBanner(
          getDictionary(Dictionary.airPaymentFailedLabel.key),
          getDictionary(Dictionary.airThereWasAnErrorProcessingYourPaymentLabel.key),
        );
      } else {
        navigate(`${urlPath}${NAVIGATION_PATHS.error}`);
      }
    },
  });
  const handleOnChangePayment = (event) => {
    setPaymentSelection(event.target.value);
    if (event.target.value === 'payNow') {
      setTimeout(() => {
        if (checkoutRef.current) {
          checkoutRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    }
  };
  const handleTermsAndConditionCheckbox = (event) => {
    setTermsAndConditionsChecked(event.target.checked);
  };
  const handleFormChange = ({ card, errors }) => {
    if (!card.error && Object.keys(errors).length === 0) {
      return setNoErrors(true);
    }
    return setNoErrors(false);
  };
  const handleShowTermsAndConditionsModal = () => {
    setOpenTermsAndConditionsModal(true);
  };
  const handleCloseTermsAndConditionsModal = () => {
    setOpenTermsAndConditionsModal(false);
  };

  const { currency } = returnFlight.cabin.totalPrice;
  const payAmount = priceChange
    ? formatPrice(priceChange.newTotal.value, currency, true, true, true)
    : formatPrice(returnFlight.cabin.totalPrice.value, currency, true, true, true);
  const disableCta = !enableCTA(termsAndConditionsChecked, paymentSelection, noErrors);
  const { isPaymentRequired, isRefundable } = returnFlight.cabin;
  const dueDate = localizedDayjs(bookingProfile.flightProfile.booking.finalPaymentDate).format(
    'MMM D[,] YYYY',
  );
  const [submitPaymentRef, setSubmitPaymentRef] = React.useState(null);
  const fullPaymentDueToday =
    new Date(bookingProfile.flightProfile.booking.finalPaymentDate) <= new Date();

  const handleSubmitPaymentRef = (submitRefVar) => {
    setSubmitPaymentRef(submitRefVar);
  };
  const handleBookFlight = () => {
    if (paymentSelection === 'reserve') {
      setPayment({
        method: paymentSelection,
      });
      bookFlight({ variables: { heldConfirmInput } });
    } else {
      submitPaymentRef.current.handleSubmit();
    }
  };

  useEffect(() => {
    if (isPaymentRequired || fullPaymentDueToday) {
      setPaymentSelection('payNow');
      trackPaymentView(true);
    } else {
      trackPaymentView(false);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <AirLoader
        open={bookLoading || isLoading}
        status={bookLoading && getDictionary(Dictionary.loaderCompletingReservation.key)}
      />
      <div className={classes.cardHeaderCheckout}>
        <div className={classes.cardTitleCheckout}>
          {getDictionary(Dictionary.airPaymentCheckoutTitleLabel.key)}
        </div>
      </div>
      <div className={classes.cardContainerCheckoutWrapper}>
        <div className={classes.cardContainerCheckout}>
          <div className={classes.paymentOptionsLabel}>
            {getDictionary(Dictionary.airPaymentCheckoutPaymentOptionsLabel.key)}
          </div>
          <RadioGroup
            aria-label="payment"
            name="payment"
            value={paymentSelection}
            onChange={handleOnChangePayment}>
            <div
              className={clsx(
                classes.info,
                isPaymentRequired && classes.infoNoMarginBottom,
                paymentSelection === 'payNow' && classes.infoSelected,
              )}
              ref={checkoutRef}>
              <div className={classes.infoWrapper}>
                <div className={clsx(classes.infoExtraTitleShowSm)}>
                  <span className={classes.infoExtraTitle}>{payAmount}</span>
                </div>
                <div className={classes.infoTitle}>
                  {getDictionary(Dictionary.airPaymentCheckoutPayTotalBalanceLabel.key)}
                </div>
                {!fullPaymentDueToday && isRefundable ? (
                  <div className={classes.infoDate}>
                    {getDictionary(Dictionary.airPaymentCheckoutRefundableByLabel.key)}
                    <span>&nbsp;{dueDate}</span>
                  </div>
                ) : (
                  ''
                )}
                {fullPaymentDueToday ? (
                  <div className={classes.infoDate}>
                    {getDictionary(Dictionary.airFullPaymentDueTodayLabel.key)}
                  </div>
                ) : (
                  ''
                )}
              </div>
              <div className={classes.infoSelect}>
                <span className={clsx(classes.infoExtraTitle, classes.infoExtraTitleHideSm)}>
                  {payAmount}
                </span>
                <FormControlLabel
                  data-testid="paynow-radio-btn"
                  value="payNow"
                  className={classes.formControlLabel}
                  control={<Radio className={classes.infoRadioButton} />}
                />
              </div>
            </div>
            {!isPaymentRequired && !fullPaymentDueToday ? (
              <div
                className={clsx(
                  classes.info,
                  paymentSelection === 'reserve' && classes.infoSelected,
                )}>
                <div className={classes.infoWrapper}>
                  <div className={clsx(classes.infoExtraTitleShowSm)}>
                    <span className={classes.infoExtraTitle}>
                      {getDictionary(Dictionary.airPaymentCheckoutReserveLabel.key)}
                    </span>
                  </div>
                  <div className={classes.infoTitle}>
                    {getDictionary(Dictionary.airPaymentCheckoutBookNowLabel.key)}
                  </div>
                  <div className={classes.infoDate}>
                    {getDictionary(Dictionary.airDueLabel.key)}
                    &nbsp;
                    {dueDate}
                  </div>
                </div>
                <div className={classes.infoSelect}>
                  <span className={clsx(classes.infoExtraTitle, classes.infoExtraTitleHideSm)}>
                    {getDictionary(Dictionary.airPaymentCheckoutReserveLabel.key)}
                  </span>
                  <FormControlLabel
                    data-testid="reserve-radio-btn"
                    value="reserve"
                    className={classes.formControlLabel}
                    control={<Radio className={classes.infoRadioButton} />}
                  />
                </div>
              </div>
            ) : (
              ''
            )}
            <div
              className={clsx(
                classes.creditCardInformation,
                paymentSelection === 'payNow'
                  ? classes.creditCardInformationOpen
                  : classes.creditCardInformationClose,
              )}>
              <div className={classes.creditCardInfoLabel}>
                {getDictionary(Dictionary.airPaymentCheckoutCreditCardInfoLabel.key)}
              </div>
              <CreditCardInformationForm
                onFormChange={handleFormChange}
                paymentSelection={paymentSelection}
                handleSubmitForm={(submitRefVar) => {
                  handleSubmitPaymentRef(submitRefVar);
                }}
                amountToPay={
                  priceChange ? priceChange.newTotal.value : returnFlight.cabin.totalPrice.value
                }
                heldConfirmInput={heldConfirmInput}
                bookFlight={bookFlight}
                bookingProfile={bookingProfile}
                setIsLoading={setIsLoading}
                showCancelPaywayBanner={showCancelPaywayBanner}
                showErrorvalidationBanner={showErrorvalidationBanner}
                payment3dsToggle={payment3dsToggle}
                royalPayway={royalPayway}
                onFinishCreditCardFormSubmit={() => onFinishPayment()}
              />
            </div>
          </RadioGroup>
        </div>
        <div className={classes.submitBox}>
          <span className={classes.formWrapper}>
            <Checkbox
              checked={termsAndConditionsChecked}
              className={classes.termsAndConditionsCheckbox}
              onChange={handleTermsAndConditionCheckbox}
              classes={{ root: classes.customCheckboxRoot }}
              inputProps={{
                'aria-label': 'primary checkbox',
                'data-testid': 'terms-and-conditions-checkbox',
              }}
            />
            <span className={classes.termsAndConditions}>
              <span>{getDictionary(Dictionary.airPaymentCheckoutIAgreeToLabel.key)}</span>
              &nbsp;
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <span
                data-testid="open-terms-and-conditions"
                className={classes.termsAndConditionsLink}
                onClick={handleShowTermsAndConditionsModal}
                role="button"
                onKeyDown={handleShowTermsAndConditionsModal}
                tabIndex="0">
                {getDictionary(Dictionary.airPaymentCheckoutTermsAndConditionsLabel.key)}
              </span>
            </span>
          </span>
          <Button
            onClick={handleBookFlight}
            data-testid="submit-checkout"
            className={classes.submitbutton}
            type="submit"
            disabled={disableCta}
            variant="contained"
            color="primary">
            {paymentSelection === 'payNow'
              ? getDictionary(Dictionary.airPaymentCheckoutPayLabel.key)
                  .concat(' ')
                  .concat(payAmount)
              : getDictionary(Dictionary.airPaymentCheckoutBookFlightsButton.key)}
          </Button>
        </div>
      </div>
      <TermsAndConditionsModal
        openModal={openTermsAndConditionsModal}
        handleClose={handleCloseTermsAndConditionsModal}
      />
    </>
  );
};
